import { useCallback, useEffect, useState } from 'react';
import { api, downloadFile, errorText, formatDate, formatDateTime } from '../api';
import { Card, Empty, Loading, Notice, TextField } from '../components/ui';

interface ExportTabelle {
  datei: string;
  name: string;
  saetze: number;
}

interface Vorschau {
  von: string;
  bis: string;
  tabellen: ExportTabelle[];
  saetzeGesamt: number;
}

interface Protokoll {
  id: string;
  period_start: string;
  period_end: string;
  filename: string;
  table_count: number;
  record_count: number;
  sha256: string;
  created_at: string;
  created_by_name: string | null;
}

const jahresbeginn = () => `${new Date().getFullYear()}-01-01`;
const heute = () => new Date().toISOString().slice(0, 10);

export function AuditPage() {
  const [zeitraum, setZeitraum] = useState({ from: jahresbeginn(), to: heute() });
  const [vorschau, setVorschau] = useState<Vorschau | null>(null);
  const [protokoll, setProtokoll] = useState<Protokoll[] | null>(null);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      setProtokoll(await api<Protokoll[]>('/api/gobd/exports'));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function pruefen() {
    setBusy(true);
    setError('');
    setVorschau(null);
    try {
      setVorschau(await api<Vorschau>('/api/gobd/preview', { method: 'POST', body: zeitraum }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function erzeugen() {
    setBusy(true);
    setError('');
    try {
      await downloadFile('/api/gobd/export', { method: 'POST', body: zeitraum });
      setMessage('Datenträger erzeugt und heruntergeladen. Die Übergabe ist protokolliert.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!protokoll) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card title="Datenträgerüberlassung nach § 147 Abs. 6 AO">
        <div className="small muted" style={{ marginBottom: 12 }}>
          Der Prüfer hat drei Zugriffsarten zur Auswahl, und die Wahl liegt bei ihm.
          <br />
          <strong>Z1</strong> — er liest selbst im System, dafür braucht er einen eigenen Zugang.{' '}
          <strong>Z2</strong> — er sagt, was ausgewertet werden soll, ihr wertet aus.{' '}
          <strong>Z3</strong> — er bekommt die Daten auf einem Datenträger. Das erzeugt diese Seite.
        </div>
        <div className="small muted" style={{ marginBottom: 12 }}>
          Das Archiv enthält je Tabelle eine CSV-Datei und eine <code>index.xml</code>, die jede
          Spalte mit Datentyp, Nachkommastellen und Datumsformat beschreibt — ohne sie kann die
          Prüfsoftware die Dateien nicht einlesen. Übergeben wird nur der gewählte Zeitraum.
        </div>

        <div className="form-grid">
          <TextField
            label="Zeitraum von"
            type="date"
            value={zeitraum.from}
            onChange={(v) => setZeitraum({ ...zeitraum, from: v })}
          />
          <TextField
            label="bis"
            type="date"
            value={zeitraum.to}
            onChange={(v) => setZeitraum({ ...zeitraum, to: v })}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <button onClick={pruefen} disabled={busy}>
            {busy ? 'Zähle …' : 'Umfang prüfen'}
          </button>{' '}
          <button className="primary" onClick={erzeugen} disabled={busy}>
            Datenträger erzeugen
          </button>
        </div>

        {vorschau && (
          <>
            <table style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Tabelle</th>
                  <th>Datei</th>
                  <th className="num">Sätze</th>
                </tr>
              </thead>
              <tbody>
                {vorschau.tabellen.map((tabelle) => (
                  <tr key={tabelle.datei}>
                    <td>{tabelle.name}</td>
                    <td className="small mono">{tabelle.datei}</td>
                    <td className="num">{tabelle.saetze.toLocaleString('de-DE')}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2} style={{ textAlign: 'right', fontWeight: 600 }}>
                    Zusammen
                  </td>
                  <td className="num" style={{ fontWeight: 600 }}>
                    {vorschau.saetzeGesamt.toLocaleString('de-DE')}
                  </td>
                </tr>
              </tbody>
            </table>
            {vorschau.saetzeGesamt === 0 && (
              <Notice kind="info">Im gewählten Zeitraum gibt es nichts zu übergeben.</Notice>
            )}
          </>
        )}
      </Card>

      <Card title="Bisherige Übergaben" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Wer wann welche Daten herausgegeben hat, muss später feststellbar sein — gegenüber der
          Finanzverwaltung wie gegenüber den Auftraggebern, deren Daten mit herausgehen. Die
          Prüfsumme belegt, welcher Stand übergeben wurde.
        </div>
        {protokoll.length === 0 ? (
          <Empty>Noch keine Datenträgerüberlassung.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Erzeugt</th>
                <th>Zeitraum</th>
                <th>Datei</th>
                <th className="num">Tabellen</th>
                <th className="num">Sätze</th>
                <th>Prüfsumme</th>
                <th>Durch</th>
              </tr>
            </thead>
            <tbody>
              {protokoll.map((eintrag) => (
                <tr key={eintrag.id}>
                  <td className="small muted">{formatDateTime(eintrag.created_at)}</td>
                  <td className="small">
                    {formatDate(eintrag.period_start)} – {formatDate(eintrag.period_end)}
                  </td>
                  <td className="small mono">{eintrag.filename}</td>
                  <td className="num">{eintrag.table_count}</td>
                  <td className="num">{eintrag.record_count.toLocaleString('de-DE')}</td>
                  <td className="small mono" title={eintrag.sha256}>
                    {eintrag.sha256.slice(0, 12)}…
                  </td>
                  <td className="small">{eintrag.created_by_name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
